<!--@format-->

<template>
    <div class="managed-storefront-widget" v-cloak>
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Manage Your StoreFront</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>

            <div class="content-wrapper">
                <div v-if="isLoading" class="loading-card">
                    <loading-svg />
                </div>
                <div v-else class="widget-container">
                    <p>
                        Below you'll be able to chose between editing the visuals of your storefront
                        as well as any kind of legal language you'd like to add for those attempting
                        to apply to it.
                    </p>
                    <div class="appearance-container">
                        <div class="title">
                            <div class="check-container" :class="isAppearanceDone ? 'green' : null">
                                <i21-checkmark-circle-svg v-if="isAppearanceDone" />
                                <r3-checkmark-outline-svg v-else />
                            </div>
                            Edit Appearance
                        </div>
                        <div class="content-container">
                            <img
                                class="widget-img"
                                src="../../assets/images/managed-storefront/edit-apperance-img.png"
                            />
                            <app-button class="edit-btn" @click="routeToDesigner">
                                <r7-edit-v2-svg />
                            </app-button>
                        </div>
                    </div>
                    <div class="disclaimer-container">
                        <div class="title">
                            <div class="check-container" :class="isAppearanceDone ? 'green' : null">
                                <i21-checkmark-circle-svg v-if="isDisclaimerDone" />
                                <r3-checkmark-outline-svg v-else />
                            </div>
                            Edit Disclaimer
                        </div>
                        <div class="content-container" :class="isDisclaimerDone ? 'green' : null">
                            <img
                                class="widget-img"
                                src="../../assets/images/managed-storefront/edit-disclaimer-img.png"
                            />
                            <app-button class="edit-btn" @click="openEditMode">
                                <r7-edit-v2-svg />
                            </app-button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        v-if="storefront.isActive"
                        class="save-btn"
                        :disabled="true"
                        @click="applyStorefront"
                    >
                        Published
                    </app-button>
                    <app-button
                        v-else-if="status === 'pending'"
                        class="save-btn"
                        :disabled="true"
                        @click="applyStorefront"
                    >
                        Approval Pending...
                    </app-button>
                    <app-button
                        v-else-if="!storefront.isActive"
                        class="save-btn"
                        :disabled="!isComplete"
                        @click="applyStorefront"
                    >
                        {{ storefront.isActive ? 'Published' : 'Publish Storefront' }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const I21CheckmarkCircleSvg = () => import('@/components/svg/grid/i21-checkmark-circle-svg');
const R3CheckmarkOutlineSvg = () => import('@/components/svg/grid/r3-checkmark-outline-svg');
const R7EditV2Svg = () => import('@/components/svg/grid/r7-edit-v2-svg');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');

import { StorefrontService } from '@/scripts/services/storefront/StorefrontService';

import EventBus from '@/scripts/event-bus';
import AppPopup from '@/components/popups/app-popup';

export default {
    name: 'managed-storefront-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'i21-checkmark-circle-svg': I21CheckmarkCircleSvg,
        'r3-checkmark-outline-svg': R3CheckmarkOutlineSvg,
        'r7-edit-v2-svg': R7EditV2Svg,
        'loading-svg': LoadingSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            storefront: {
                disclaimer: null,
                logoImageUrl: null,
                description: null,
                resources: null,
            },

            isLoading: true,
            status: null,
        };
    },

    computed: {
        isComplete() {
            return this.isAppearanceDone && this.isDisclaimerDone;
        },

        isAppearanceDone() {
            return (
                this.storefront.logoImageUrl &&
                this.storefront.description &&
                this.storefront.resources.length > 0
            );
        },

        isDisclaimerDone() {
            return this.storefront.disclaimer;
        },

        isAgencyUser() {
            return this.$store.getters.isAgencyUser;
        },
    },

    created() {
        this.getStorefront().then(() => {
            if (!this.storefront.isActive) {
                this.getApplicationStatus();
            }
            this.isLoading = false;
        });

        EventBus.$on('update-disclaimer', this.getStorefront);
    },

    beforeDestroy() {
        EventBus.$off('update-disclaimer', this.getStorefront);
    },

    watch: {},

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        openEditMode() {
            EventBus.$emit('open-widget', {
                name: 'marketplace-disclaimer-widget',
                data: {
                    agencyId: this.widget.agencyId,
                    disclaimer: this.storefront.disclaimer,
                },
            });
        },

        routeToDesigner() {
            var baseUrl = this.isAgencyUser ? '' : `/Agencies/${this.widget.agencyId}`;
            this.$router.push(`${baseUrl}/Storefront/Design`);
            this.closeWidget();
        },

        getStorefront() {
            return new Promise((resolve, reject) => {
                StorefrontService.getById(this.widget.agencyId)
                    .then((response) => {
                        this.populateStoreData(response);
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching information.'
                        );
                        reject();
                    });
            });
        },

        getApplicationStatus() {
            StorefrontService.getAgencyApplication(this.widget.agencyId)
                .then((response) => {
                    if (response.data.data.length > 0) {
                        this.status =
                            response.data.data[0].Status &&
                            response.data.data[0].Status.toLowerCase() !== 'denied'
                                ? 'pending'
                                : null;
                    }
                })
                .catch((e) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching information.'
                    );
                });
        },

        populateStoreData(response) {
            const data = response.data.data;
            if (data.storefront[0].UpdatedAt) {
                const featuredItems = JSON.parse(data.storefront[0].FeactureItemJson);

                this.storefront.disclaimer = data.storefront[0].Disclaimer;
                this.storefront.logoImageUrl = data.storefront[0].LogoImageUrl;
                this.storefront.description = data.storefront[0].OfferDescription;
                this.storefront.resources = Array.isArray(featuredItems)
                    ? featuredItems.filter((i) => i.id)
                    : [];

                this.storefront.isActive = data.storefront[0].IsActive;
            }
        },

        showSuccessPopup() {
            const actions = {
                tertiary: {
                    label: 'Close',
                    action: () => {},
                },
            };

            this.$modal.show(
                AppPopup,
                {
                    type: 'success-popup',
                    title: 'Success',
                    content: `Your page has been submitted! A member of GlassHive will review your proposed storefront. You'll receive an email once the process is complete.  `,
                    actions: actions,
                    animation: 'confetti',
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                }
            );
        },

        applyStorefront() {
            StorefrontService.apply(this.widget.agencyId)
                .then(() => {
                    this.showSuccessPopup();
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving information.'
                    );
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>

<style>
.managed-storefront-widget .ab-test-container .content-container {
    padding: 0 60px;
}

.managed-storefront-widget .content-wrapper {
    overflow-y: hidden;
}

.managed-storefront-widget .appearance-container {
    position: relative;
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    margin-bottom: 25px;
    margin-top: 25px;
}

.managed-storefront-widget .disclaimer-container {
    position: relative;
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    margin-bottom: 25px;
    margin-top: 25px;
}

.managed-storefront-widget .appearance-container .title {
    justify-content: center;
    display: flex;
    position: absolute;
    top: -18px;
    left: 200px;
    padding: 7px 43px;
    background-color: #ffffff;
    font-weight: 600;
    font-size: 16px;
}

.managed-storefront-widget .disclaimer-container .title {
    justify-content: center;
    display: flex;
    position: absolute;
    top: -18px;
    left: 200px;
    padding: 7px 43px;
    background-color: #ffffff;
    font-weight: 600;
    font-size: 16px;
}

.managed-storefront-widget .content-container {
    padding: 30px;
}

.managed-storefront-widget .r3-checkmark-outline-svg path {
    fill: #cac6cb;
}

.managed-storefront-widget .widget-img {
    height: auto;
    width: 60%;
    margin-left: 100px;
}

.managed-storefront-widget .edit-btn {
    height: 35px !important;
    border-radius: 30px !important;
    padding: 0px 9px !important;
    float: right;
    position: relative;
    right: 4vw;
    top: 0.5vw;
}

.managed-storefront-widget .r7-edit-v2-svg path {
    fill: #fff;
}

.managed-storefront-widget .cancel-btn {
    border: 1px solid #cac6cb;
    border-radius: 20px;
    background: transparent;
    font: normal normal medium 14px/25px Roboto;
    letter-spacing: 0px;
    color: #382153;
    width: 127px !important;
    height: 40px !important;
}

.managed-storefront-widget .cancel-btn:hover {
    background: #dcdcdc;
    border: 2px solid #cac6cb;
}

.managed-storefront-widget .check-container {
    width: 25px;
    height: 25px;
    margin-right: 10px;
    display: flex;
    align-items: center;
    justify-content: space-around;
    border-radius: 50%;
    border: 1px solid #cac6cb;
}

.managed-storefront-widget .check-container.green {
    border: none;
}

.managed-storefront-widget .loading-screen {
    margin-left: 0px;
}
</style>
